import { CrudList } from '../shared/CrudPage';
import { stockTransferService } from '@kss/api';
import type { StockTransfer, StockTransferCreate, StockTransferFilter } from '@kss/api';
import { StatusBadge } from '@kss/ui';
import { NameCell } from '../../hooks/useNameMaps';

/* SSO ISSUE 6 (2026-07-25) — Added an Items column that surfaces the
   line count + total-unit tally on the list. Full per-item breakdown
   (product picker + qty + on-hand-at-source hint) lives on the edit
   form; row-click still navigates there via the Edit action, so the
   list serves as the summary and the form as the details preview. */

export default function AllStockTransfers() {
  return (
    <CrudList<StockTransfer, StockTransferCreate, StockTransferFilter>
      title="Stock transfers"
      newLabel="New transfer"
      service={stockTransferService}
      idKey="stockTransferId"
      labelKey="transferNumber"
      basePath="/panel/stock-transfers"
      // Newest transfer on top — order-like grid. Ref: ORDER_WORKFLOW_AUDIT #6.
      initialSortStatus={{ columnAccessor: 'transferDate', direction: 'desc' }}
      columns={[
        { accessor: 'transferNumber', title: 'Transfer #', sortable: true, filterable: true, width: 160 },
        {
          accessor: 'transferDate',
          title: 'Date',
          sortable: true,
          render: (r) => (r.transferDate ? new Date(r.transferDate).toLocaleDateString() : '—'),
        },
        // From/To warehouse columns now resolve to the warehouse name;
        // the raw id stays on the row for save round-tripping.
        {
          accessor: 'fromWarehouseId',
          title: 'From',
          render: (r) => <NameCell kind="warehouse" id={r.fromWarehouseId} />,
        },
        {
          accessor: 'toWarehouseId',
          title: 'To',
          render: (r) => <NameCell kind="warehouse" id={r.toWarehouseId} />,
        },
        {
          // Not a real column on the row (items are hydrated separately);
          // reuse `notes` as a harmless accessor for the DataGrid's typing.
          accessor: 'notes',
          title: 'Items',
          width: 120,
          render: (r) => {
            const items = r.items ?? [];
            if (items.length === 0) return <span className="text-content-muted">—</span>;
            const totalQty = items.reduce((acc, i) => acc + Number(i.quantity ?? 0), 0);
            return (
              <span className="text-sm text-content-primary">
                {items.length} line{items.length === 1 ? '' : 's'}{' '}
                <span className="text-content-secondary">({totalQty} units)</span>
              </span>
            );
          },
        },
        {
          accessor: 'status',
          title: 'Status',
          sortable: true,
          render: (r) => <StatusBadge status={r.status ?? '—'} />,
        },
      ]}
    />
  );
}
